import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSections, isSectionOn } from "@/hooks/useSections";
import { toast } from "sonner";
import RegulatoryBadges from "@/components/RegulatoryBadges";

export default function Campaigns() {
  const { t, lang, pick } = useI18n();
  const sections = useSections();
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", reason: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/campaigns").then((r) => setCampaigns(
    (r.data || []).filter((c) => c.active !== false && c.is_archived !== true)
  ));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/campaigns/${selected.id}/apply`, { campaign_id: selected.id, ...form });
      toast.success(t("common.thankYou"));
      setSelected(null);
      setForm({ name: "", email: "", phone: "", company: "", reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isSectionOn(sections, "campaigns")) return (
    <div className="container-x py-24 text-navy/60" data-testid="campaigns-off">
      {lang === "ar" ? "هذا القسم غير متاح حالياً." : "This section is currently unavailable."}
    </div>
  );

  return (
    <div data-testid="campaigns-page">
      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow">{t("nav.campaigns")}</span>
          <h1 className="mt-4 text-4xl md:text-6xl">
            {lang === "ar" ? "حملات حوكمة وامتثال متخصصة" : "Specialized Governance & Compliance Campaigns"}
          </h1>
          <span className="divider-gold" />
          <p className="text-cream/80 max-w-2xl">
            {lang === "ar"
              ? "مبادرات مركّزة لدعم المؤسسات والمهنيين في مجالات الحوكمة وحماية البيانات والامتثال التنظيمي."
              : "Focused initiatives supporting organizations and professionals in governance, data protection, and regulatory compliance."}
          </p>
        </div>
      </section>

      <section className="container-x py-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        {campaigns.map((c) => {
          const remaining = (c.total_slots || 0) - (c.filled_slots || 0);
          const pct = Math.round(((c.filled_slots || 0) / (c.total_slots || 1)) * 100);
          const soon = c.status === "coming_soon";
          const showSeats = c.show_seat_count !== false;
          return (
            <article key={c.id} data-testid={`campaign-card-${c.id}`} className={`card-elev p-8 ${soon ? "opacity-90" : ""}`}>
              {/* Regulatory framework badges — top of card, before title, per design */}
              <RegulatoryBadges value={c.regulatory_frameworks} lang={lang} testId={`campaign-reg-${c.id}`} />
              <div className="flex items-start justify-between gap-4 mt-3">
                <h2 className="text-2xl text-navy leading-snug">{pick(c, "title")}</h2>
                {soon ? (
                  <span className="chip !bg-amber-400 !text-navy !border-amber-500" data-testid={`camp-soon-${c.id}`}>
                    {lang === "ar" ? "قريباً" : "Coming Soon"}
                  </span>
                ) : showSeats ? (
                  <span className="chip !bg-navy !text-gold" data-testid={`camp-seats-${c.id}`}>
                    <span className="w-2 h-2 rounded-full bg-gold animate-pulse-slow" />
                    {remaining} {t("common.slotsLeft")}
                  </span>
                ) : null}
              </div>
              <p className="mt-4 text-navy/75 leading-relaxed">{pick(c, "desc")}</p>
              {!soon && showSeats && (
                <>
                  <div className="mt-6 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-navy/60">
                    {c.filled_slots}/{c.total_slots} {lang === "ar" ? "مقعد" : "seats"}
                  </div>
                </>
              )}
              {c.cta_show !== false && (
                <button
                  data-testid={`apply-btn-${c.id}`}
                  onClick={() => !soon && setSelected(c)}
                  disabled={soon || (showSeats && remaining <= 0)}
                  className="mt-6 btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {(() => {
                    const custom = lang === "ar" ? c.cta_text_ar : c.cta_text_en;
                    if (custom) return custom;
                    if (soon) return lang === "ar" ? "قريباً" : "Coming soon";
                    return (!showSeats || remaining > 0) ? t("common.apply") : t("common.filled");
                  })()}
                </button>
              )}
            </article>
          );
        })}
      </section>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <form
            data-testid="apply-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-cream max-w-lg w-full p-8 rounded-md border border-gold/30 animate-fade-up"
          >
            <h3 className="text-2xl text-navy">{pick(selected, "title")}</h3>
            <span className="divider-gold" />
            <div className="space-y-4">
              <input required data-testid="apply-name" placeholder={t("common.name")} className="field-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input required type="email" data-testid="apply-email" placeholder={t("common.email")} className="field-box" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="apply-phone" placeholder={t("common.phone")} className="field-box" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input data-testid="apply-company" placeholder={t("common.company")} className="field-box" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <textarea data-testid="apply-reason" placeholder={
                (lang === "ar" ? selected.apply_question_ar : selected.apply_question_en)
                || (lang === "ar" ? "لماذا ترغب بالانضمام؟" : "Why do you want to join?")
              } rows={3} className="field-box" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setSelected(null)} className="text-navy/70 px-3 py-2">
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button type="submit" disabled={loading} data-testid="apply-submit" className="btn-gold disabled:opacity-40">
                {loading ? "..." : t("common.submit")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
